import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ContactModal: React.FC<ContactModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [subject, setSubject] = useState('');
  const [email, setEmail] = useState(user?.email || '');
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendStatus, setSendStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSending(true);
    setSendStatus('idle');

    try {
      const response = await fetch('/api/contact/send-message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subject,
          email,
          message,
          userId: user?.uid || null
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send message');
      }

      setSendStatus('success');
      
      // Reset form and close modal after success
      setTimeout(() => {
        setSubject('');
        setMessage('');
        if (!user) setEmail('');
        setSendStatus('idle');
        onClose();
      }, 2000);
      
    } catch (error) {
      console.error('Error sending message:', error);
      setSendStatus('error');
    } finally {
      setIsSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div 
        className="rounded-lg shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col"
        style={{ backgroundColor: '#161616' }}
        onClick={e => e.stopPropagation()}
      >
        <header className="flex justify-between items-center p-4 border-b border-gray-700">
          <h2 className="text-xl font-bold text-white">Contact Us</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </header>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Subject Field */}
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              Subject *
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
              className="w-full bg-gray-700 border border-gray-600 rounded-md p-3 text-white text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none"
              placeholder="What's this about?"
            />
          </div>

          {/* Email Field (only if not logged in) */}
          {!user && (
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Your Email *
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full bg-gray-700 border border-gray-600 rounded-md p-3 text-white text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                placeholder="your.email@example.com"
              />
            </div>
          )}

          {user && (
            <div className="text-sm text-gray-400">
              Sending from: <span className="text-cyan-400">{user.email}</span>
            </div>
          )}

          {/* Message Field */}
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              Your Message *
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              rows={6}
              className="w-full bg-gray-700 border border-gray-600 rounded-md p-3 text-white text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none resize-none"
              placeholder="Tell us what's on your mind..."
            />
          </div>

          {/* Status Messages */}
          {sendStatus === 'success' && (
            <div className="bg-green-500/20 border border-green-500/50 rounded-md p-3 text-green-400 text-sm">
              Message sent successfully! We'll get back to you soon.
            </div>
          )}
          {sendStatus === 'error' && (
            <div className="bg-red-500/20 border border-red-500/50 rounded-md p-3 text-red-400 text-sm">
              Failed to send message. Please try again.
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSending}
            className="w-full bg-cyan-500 hover:bg-cyan-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-lg transition-colors"
          >
            {isSending ? 'Sending...' : 'Send Message'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ContactModal;
